/**
 * Input sanitization utilities for LifeFlo AI
 * Uses DOMPurify for XSS prevention
 */
import DOMPurify from 'dompurify';

/**
 * Sanitize user input to prevent XSS attacks
 * @param {string} input - Raw user input
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input, maxLength = 10000) => {
  if (!input || typeof input !== 'string') return '';
  
  // Truncate if too long
  let sanitized = input.slice(0, maxLength);
  
  // Use DOMPurify to strip dangerous content
  sanitized = DOMPurify.sanitize(sanitized, {
    ALLOWED_TAGS: [], // Strip all HTML tags
    ALLOWED_ATTR: [], // Strip all attributes
  });
  
  return sanitized.trim();
};

/**
 * Sanitize a name field (more restrictive)
 * @param {string} name - Raw name input
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} Sanitized name
 */
export const sanitizeName = (name, maxLength = 100) => {
  if (!name || typeof name !== 'string') return '';
  
  let sanitized = sanitizeInput(name, maxLength);
  
  // Only allow alphanumeric, spaces, hyphens, apostrophes
  sanitized = sanitized.replace(/[^\w\s'\-.,]/g, '');
  
  return sanitized.trim();
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Whether email is valid
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {{ valid: boolean, message: string }} Validation result
 */
export const validatePassword = (password) => {
  if (!password) {
    return { valid: false, message: 'Password is required' };
  }
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters' };
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Password must contain an uppercase letter' };
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Password must contain a lowercase letter' };
  }
  if (!/\d/.test(password)) {
    return { valid: false, message: 'Password must contain a number' };
  }
  return { valid: true, message: '' };
};

/**
 * Validate positive number
 * @param {number} value - Value to validate
 * @param {string} fieldName - Field name for error message
 * @param {number} maxValue - Maximum allowed value
 * @returns {{ valid: boolean, message: string }} Validation result
 */
export const validatePositiveNumber = (value, fieldName, maxValue = null) => {
  if (value < 0) {
    return { valid: false, message: `${fieldName} cannot be negative` };
  }
  if (maxValue !== null && value > maxValue) {
    return { valid: false, message: `${fieldName} cannot exceed ${maxValue}` };
  }
  return { valid: true, message: '' };
};

/**
 * Validate required string
 * @param {string} value - Value to validate
 * @param {string} fieldName - Field name for error message
 * @param {number} minLength - Minimum length
 * @param {number} maxLength - Maximum length
 * @returns {{ valid: boolean, message: string }} Validation result
 */
export const validateRequiredString = (value, fieldName, minLength = 1, maxLength = 500) => {
  if (!value || !value.trim()) {
    return { valid: false, message: `${fieldName} is required` };
  }
  const trimmed = value.trim();
  if (trimmed.length < minLength) {
    return { valid: false, message: `${fieldName} must be at least ${minLength} character(s)` };
  }
  if (trimmed.length > maxLength) {
    return { valid: false, message: `${fieldName} cannot exceed ${maxLength} characters` };
  }
  return { valid: true, message: '' };
};

export default {
  sanitizeInput,
  sanitizeName,
  isValidEmail,
  validatePassword,
  validatePositiveNumber,
  validateRequiredString,
};
